<script lang="ts">
  import { indent, lineCount, newlineWithIndent, outdent, type TextEdit } from './editor-text';
  import { createLineHighlighter, type Token } from './highlight/swift';
  import { isSubmitChord } from './keyboard';

  interface Props {
    value?: string;
    language?: 'swift';
    readonly?: boolean;
    highlightLines?: number[];
    errorLines?: { line: number; message: string }[];
    minLines?: number;
    label?: string;
    id?: string;
    onchange?: ((value: string) => void) | undefined;
    onsubmit?: (() => void) | undefined;
  }

  const uid = $props.id();

  let {
    value = $bindable(''),
    language = 'swift',
    readonly = false,
    highlightLines = [],
    errorLines = [],
    minLines = 6,
    label = 'Swift のコード',
    id = `code-editor-${uid}`,
    onchange,
    onsubmit,
  }: Props = $props();

  let textarea: HTMLTextAreaElement | undefined = $state();
  let layer: HTMLPreElement | undefined = $state();
  /** IME の未確定文字の入力中。この間はハイライト層を隠し、textarea 自身の文字を見せる */
  let composing = $state(false);
  /** Esc の直後だけ、Tab をフォーカス移動に使う（Esc → Tab で抜ける） */
  let escaped = $state(false);

  const lines = $derived(Math.max(lineCount(value), minLines));
  const errorByLine = $derived(new Map(errorLines.map((e) => [e.line, e.message])));
  const highlightSet = $derived(new Set(highlightLines));
  const errorsId = $derived(`${id}-errors`);
  const helpId = $derived(`${id}-help`);

  // ハイライト層（textarea の背面に重ねる <pre>）。行単位のキャッシュ付きで、変わった行だけ再計算する
  const highlighter = createLineHighlighter();
  let lastTokens: Token[][] = [];
  const tokenLines = $derived.by(() => {
    // 変換中は再計算しない（層は隠れている）。確定したら最新の value で描き直す
    if (composing) return lastTokens;
    lastTokens = highlighter(value);
    return lastTokens;
  });

  /**
   * textarea のスクロール量を層の <code> の transform に写す。層の scrollLeft に写すと、
   * Safari のラバーバンド（端での行き過ぎ。scrollLeft が負や最大超えになる）や最大値の差で
   * 層だけ止まり、色が文字からずれる。transform なら行き過ぎた量もそのまま追える。
   * element.style は CSSOM なので CSP（style-src 'self'）の対象外。
   */
  function syncScroll() {
    const code = layer?.firstElementChild;
    if (!textarea || !(code instanceof HTMLElement)) return;
    code.style.transform = `translate(${-textarea.scrollLeft}px, ${-textarea.scrollTop}px)`;
  }

  // 描き直したあとも位置を合わせる（行が減って textarea 側のスクロールが詰まった場合など）
  $effect(() => {
    void tokenLines;
    syncScroll();
  });

  function oncompositionstart() {
    composing = true;
  }

  function oncompositionend(e: CompositionEvent) {
    const el = e.currentTarget;
    composing = false;
    if (el instanceof HTMLTextAreaElement && el.value !== value) {
      value = el.value;
      onchange?.(value);
    }
  }

  function apply(edit: TextEdit) {
    value = edit.value;
    onchange?.(value);
    const el = textarea;
    if (!el) return;
    el.value = edit.value;
    el.setSelectionRange(edit.selectionStart, edit.selectionEnd);
  }

  function onkeydown(e: KeyboardEvent) {
    const el = e.currentTarget;
    if (!(el instanceof HTMLTextAreaElement)) return;
    if (isSubmitChord(e)) {
      e.preventDefault();
      e.stopPropagation();
      onsubmit?.();
      return;
    }
    if (e.key === 'Escape') {
      // カード側の「Esc で中断」に流さず、次の Tab でエディタから出られるようにする
      escaped = true;
      e.stopPropagation();
      return;
    }
    if (readonly) return;
    if (e.key === 'Tab' && !escaped && !e.altKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      const { selectionStart: s, selectionEnd: t } = el;
      apply(e.shiftKey ? outdent(el.value, s, t) : indent(el.value, s, t));
      return;
    }
    // Safari は確定の Enter を compositionend の後に isComposing=false・keyCode 229 で送る
    const imeEnter = e.isComposing || composing || e.keyCode === 229;
    if (e.key === 'Enter' && !e.shiftKey && !imeEnter) {
      e.preventDefault();
      apply(newlineWithIndent(el.value, el.selectionStart, el.selectionEnd));
      return;
    }
    if (e.key !== 'Shift') escaped = false;
  }

  function oninput(e: Event) {
    const el = e.currentTarget;
    if (!(el instanceof HTMLTextAreaElement)) return;
    value = el.value;
    onchange?.(value);
  }
</script>

<div class="editor" class:readonly data-language={language} data-testid="code-editor">
  <div class="surface">
    <ol class="gutter" aria-hidden="true">
      {#each { length: lines }, i (i)}
        {@const n = i + 1}
        <li class:hl={highlightSet.has(n)} class:err={errorByLine.has(n)}>
          {#if errorByLine.has(n)}<span class="bang">!</span>{/if}{n}
        </li>
      {/each}
    </ol>
    <div class="field">
      <!-- 見た目だけの層。読み上げ・選択・クリックは前面の textarea が受ける -->
      <!-- prettier-ignore -->
      <pre bind:this={layer} class="chroma layer" class:hidden={composing} aria-hidden="true" data-testid="code-editor-highlight"><code>{#each tokenLines as tokens, i (i)}<span class="line" class:empty={tokens.length === 0}>{#each tokens as t, j (j)}{#if t.cls}<span class={t.cls}>{t.text}</span>{:else}{t.text}{/if}{/each}{tokens.length === 0 ? ' ' : ''}</span>{/each}</code></pre>
      <textarea
        bind:this={textarea}
        {id}
        class="input"
        class:composing
        rows={lines}
        {value}
        {readonly}
        wrap="off"
        spellcheck="false"
        autocapitalize="off"
        autocomplete="off"
        aria-label={label}
        aria-multiline="true"
        aria-describedby={errorLines.length > 0 ? `${helpId} ${errorsId}` : helpId}
        aria-invalid={errorLines.length > 0 ? 'true' : undefined}
        {onkeydown}
        {oninput}
        onscroll={syncScroll}
        {oncompositionstart}
        {oncompositionend}
        onblur={() => (escaped = false)}></textarea>
    </div>
  </div>
  {#if errorLines.length > 0}
    <ul class="errors" id={errorsId}>
      {#each errorLines as err (err.line + err.message)}
        <li><span class="num">{err.line} 行目</span>: {err.message}</li>
      {/each}
    </ul>
  {/if}
  <p class="help" id={helpId}>
    {#if escaped}
      <kbd>Tab</kbd> で次の項目へ移動します。
    {:else}
      <kbd>Tab</kbd> でインデント。エディタから出るときは <kbd>Esc</kbd> のあと <kbd>Tab</kbd>。
    {/if}
  </p>
</div>

<style>
  .editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .surface {
    display: flex;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface-code);
    overflow: hidden;
    font-family: var(--font-mono);
    font-size: var(--fs-code);
    line-height: var(--lh-code);
  }
  .surface:focus-within {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: var(--focus-offset);
  }
  .gutter {
    flex: none;
    margin: 0;
    padding: var(--space-3) var(--space-2) var(--space-3) var(--space-3);
    list-style: none;
    color: var(--code-gutter);
    text-align: right;
    font-variant-numeric: tabular-nums;
    user-select: none;
    border-right: 1px solid var(--border);
    min-width: 3.5ch;
  }
  .gutter li {
    position: relative;
  }
  .gutter li.hl {
    background: var(--code-line-hl);
  }
  .gutter li.err {
    color: var(--err-text);
    font-weight: var(--fw-bold);
  }
  .bang {
    margin-right: var(--space-1);
  }
  .field {
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;
  }
  /* textarea と同じフォント・行高・パディング・タブ幅・折り返し（しない）にそろえる */
  .layer,
  .input {
    margin: 0;
    border: 0;
    border-radius: 0;
    font: inherit;
    line-height: inherit;
    letter-spacing: normal;
    font-variant-ligatures: none;
    font-kerning: none;
    tab-size: 4;
    white-space: pre;
    overflow-wrap: normal;
    text-align: left;
  }
  .layer {
    position: absolute;
    inset: 0;
    padding: 0;
    overflow: hidden;
    background: transparent;
    color: var(--syn-text);
    pointer-events: none;
    user-select: none;
  }
  .layer.hidden {
    visibility: hidden;
  }
  .layer code {
    display: block;
    box-sizing: border-box;
    width: max-content;
    min-width: 100%;
    /* 右は textarea より広く取り、横スクロールの上限が textarea より先に尽きないようにする */
    padding: var(--space-3) calc(var(--space-4) + 2ch) var(--space-3) var(--space-4);
    background: none;
    font: inherit;
    will-change: transform;
  }
  .layer .line {
    display: block;
  }
  .input {
    position: relative;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    padding: var(--space-3) var(--space-4);
    background: transparent;
    /* 文字は背面の層が描く。キャレットと選択範囲だけ textarea が描く */
    color: transparent;
    caret-color: var(--syn-text);
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior: none;
    resize: none;
  }
  .input.composing {
    color: var(--syn-text);
  }
  .input:focus-visible {
    outline: none;
  }
  @media (forced-colors: active) {
    .layer {
      display: none;
    }
    .input {
      color: CanvasText;
    }
  }
  .errors {
    margin: 0;
    padding: var(--space-2) var(--space-3);
    list-style: none;
    border-radius: var(--radius-sm);
    background: var(--err-bg);
    color: var(--err-text);
    font-size: var(--fs-sm);
  }
  .help {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
</style>
